import { Attachment, TranslatableField } from './Utils';
import { Owner } from './Posts';

export type FormQuestionType =
  | 'attachment'
  | 'checkbox'
  | 'date'
  | 'location'
  | 'money'
  | 'phone_number'
  | 'picture'
  | 'radio'
  | 'section'
  | 'text'
  | 'textarea';

export type ContextType = 'contribution' | 'questionnaire' | 'secured_strategy';

export const singletonQuestionTypes: FormQuestionType[] = ['picture', 'money'] as const;

export const alwaysPublicTypes: FormQuestionType[] = ['picture', 'money'] as const;

export const questionTypesForContext: Record<string, Array<FormQuestionType | 'divider'>> = {
  contribution: [
    'text',
    'textarea',
    'radio',
    'checkbox',
    'divider',
    'date',
    'location',
    'attachment',
    'money',
    'phone_number',
    'picture',
  ],
  questionnaire: [
    'text',
    'textarea',
    'radio',
    'checkbox',
    'divider',
    'date',
    'location',
    'attachment',
    'phone_number',
    'divider',
    'section',
  ],
  secured_strategy: ['text', 'radio', 'checkbox', 'date'],
};

type FormQuestionOptionTranslatableFields = TranslatableField<{
  label: string;
}>;

export interface FormQuestionOption extends FormQuestionOptionTranslatableFields {
  id: string;
  uuid_ref: string;
  other?: boolean;
  newRecord?: boolean; // used to determine if the question is a new record or if it's already persisted
}

export type FormQuestionTranslatableFields = TranslatableField<{
  title: string;
  description: string;
}>;

export type Operator =
  | 'eq'
  | 'not_eq'
  | 'present'
  | 'blank'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'gte'
  | 'contains'
  | 'not_contains';

export type QuestionLink = {
  id: string;
  question_id: number;
  target_ref: string;
  question_option_refs: string[];
  operator: Operator;
  value?: string | null;
};

export interface FormQuestion extends FormQuestionTranslatableFields {
  _destroy?: boolean; // used to determine if the question should be destroyed
  accepted_answers?: string;
  answers_count?: number;
  confidential: boolean;
  dashboard_filter_enabled: boolean;
  dropdown: boolean;
  field_type: FormQuestionType;
  geojson: string;
  id: number;
  maxlength?: number;
  newRecord?: boolean; // used to determine if the question is a new record or if it's already persisted
  position: number;
  post_record_attribute_key: 'budget' | null;
  public_filter_enabled: boolean;
  section_id: number | null;
  question_links: QuestionLink[];
  question_links_attributes?: QuestionLink[]; // for the form
  number_label?: string;
  question_options: FormQuestionOption[];
  question_options_attributes?: FormQuestionOption[]; // for the form
  required: boolean;
  uuid: string;
  uuid_ref: string;
}

export interface FormResponseAnswer {
  question_id: number;
  content: string;
  date: string;
  question_options: FormQuestionOption[];
  picture?: string | null;
  attachments?: Attachment[];
  hidden?: boolean;
  picture_attributes?: {
    _destroy: boolean;
  };
}

export interface FormResponse {
  id: number;
  created_at: string;
  answers: FormResponseAnswer[];
  user: Owner;
}
